import { useCallback, useEffect, useState } from 'react';
import { MappedPassengerType, PassengerFromApiType, SelectedWorkplaceType } from '../../types';
import useApiCall from '../../../../../hooks/useApiCall';
import VehiclesService from '../../../../../services/affreightment/VehiclesService';
import { useAppContext } from '../../../../../contexts/auth';
import PassengersService from '../../../../../services/affreightment/PassengersService';
import removeDuplicates from '../../../../../utils/removeDuplicates';
import { toast } from 'react-toastify';

export default function useLoadPassengers() {
  const [isLoading, setIsLoading] = useState(true);
  const [workplaces, setWorkplaces] = useState<SelectedWorkplaceType[]>([]);
  const [passengers, setPassengers] = useState<MappedPassengerType[]>([]);

  const [doesWorkplacesListExists, setDoesWorkplacesListExists] = useState(false);
  const [doesPassengersListExists, setDoesPassengersListExists] = useState(false);
  const [apiWorkplaceFetched, setApiWorkplaceFetched] = useState(false);
  const [apiPassengersFetched, setApiPassengersFetched] = useState(false);

  const [selectedWorkplace, setSelectedWorkplace] = useState<SelectedWorkplaceType>({} as SelectedWorkplaceType);
  const [goingLineOptions, setGoingLineOptions] = useState<{ value: string, label: string }[]>([]);
  const [backLineOptions, setBackLineOptions] = useState<{ value: string, label: string }[]>([]);

  const { apiCall } = useApiCall();
  const { selectedCompany, userData } = useAppContext();
  const companyCode = selectedCompany.value;
  const { userCode, userProfile } = userData;

  const getWorkplaces = useCallback(async () => {
    await apiCall({
      apiToCall: VehiclesService.getWorkplaces,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        const workplacesData: { codLocTrab: number, descrLocTrab: string }[]  = response.locaisTrabalho;
        const workplacesOptions = workplacesData?.map((wp) => (
          { value: wp.codLocTrab, label: wp.descrLocTrab }
        ));
        setWorkplaces(workplacesOptions);
        setDoesWorkplacesListExists(response.listaLT);
        setApiWorkplaceFetched(true);
      },
      catchAction: () => setApiWorkplaceFetched(false),
      catchMessage: 'Não foi possível recuperar a lista de locais de trabalho',
    });
    setIsLoading(false);
  }, [apiCall, companyCode, userCode, userProfile]);

  const statusOptions = [
    {value: '', label: 'Todos'},
    {value: 'Ativo', label: 'Ativos'},
    {value: 'Inativo', label: 'Inativos'},
  ];

  const getPassengers = useCallback(async (workplaceCode: number) => {
    await apiCall({
      apiToCall: PassengersService.listPassengers,
      queryParams: {
        codEmpresa: companyCode,
        codLocTrab: workplaceCode,
        codUsu: userCode,
        perfilUsu: userProfile,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        setApiPassengersFetched(true);
        if (!response.codigo) {
          toast.error(`Não foi possível recuperar a lista de passageiros (${response.msg})`);
          setApiPassengersFetched(false);
          return;
        }
        setDoesPassengersListExists(response.listaUsu);
        const passengersList: PassengerFromApiType[] = response?.usuariosLinhasFreta;
        if (!passengersList) {
          setPassengers([]);
          setDoesPassengersListExists(response.listaUsu);
          return;
        }
        const mappedPassengersList = passengersList?.map((psg) => ({
          employeeCode: psg.codFuncionario,
          cpf: psg.cpf,
          registration: psg.matricula,
          name: psg.nome,
          status: psg.statusCad,
          cep: psg.cep,
          streetName: psg.logradouro,
          number: psg.numero,
          district: psg.bairro,
          city: psg.cidade,
          uf: psg.uf,
          entryTime: psg.horaEntrada,
          exitTime: psg.horaSaida,
          inactiveDate: psg.dataInativoAte,
          consultCode: psg.codConsulta,
          vtValue: psg.valorVT,
          goingLineStatus: psg.StatusLinIda,
          goingLine: psg.linhaIda,
          goingLineRouteId: psg.routeIdLinhaIda,
          backLineStatus: psg.StatusLinVolta,
          backLine: psg.linhaVolta,
          backLineRouteId: psg.routeIdLinhaVolta,
        }));

        const mappedGoingLines = mappedPassengersList?.map((psg) => ({ value: psg.goingLineRouteId, label: psg.goingLine }));
        mappedGoingLines.unshift({ value: '', label: 'Todas' });
        const mappedBackLines = mappedPassengersList?.map((psg) => ({ value: psg.backLineRouteId, label: psg.backLine }));
        mappedBackLines.unshift({ value: '', label: 'Todas' });
        setGoingLineOptions(removeDuplicates(mappedGoingLines));
        setBackLineOptions(removeDuplicates(mappedBackLines));
        setPassengers(mappedPassengersList);
      },
      catchAction: () => setApiPassengersFetched(false),
      catchMessage: 'Não foi possível recuperar a lista de passageiros',
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  const handleWorkplaceChange = (wp: { value: number, label: string }) => {
    setSelectedWorkplace(wp);
    getPassengers(wp.value);
  };

  useEffect(() => {
    if (companyCode) {
      getWorkplaces();
    }
  }, [companyCode, getWorkplaces]);

  function handleTryGetWorkplacesAgain() {
    getWorkplaces();
  }

  function handleTryGetpassengersAgain(workplaceCode: number) {
    getPassengers(workplaceCode);
  }

  return {
    isLoading,
    workplaces,
    passengers,
    doesWorkplacesListExists,
    doesPassengersListExists,
    apiWorkplaceFetched,
    apiPassengersFetched,
    selectedWorkplace,
    handleWorkplaceChange,
    handleTryGetWorkplacesAgain,
    handleTryGetpassengersAgain,
    getPassengers,
    goingLineOptions,
    backLineOptions,
    statusOptions,
  };
}
